import { Box, Typography, Checkbox, FormControlLabel } from "@mui/material";
import { BlackButtonComponent, WhiteButtonComponent } from "../../components";
import useBankScreen from "./hooks/bankScreen";
import DynamicInput from "./feilds/dynamic-input";
import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  resetBankDetails,
  updateBankDetails,
  updateInitialState,
} from "slice/merchantSlice";
import { RootState } from "store";
import { verifyBankDetails } from "services/otpService";
import BankDetailsForm from "./bank-details-form/bank-details-form";
import { appendDetails } from "services/MerchantHostApi";
import { containsPropertyMerchant, getChangedFields } from "utils/index";
import { useNavigate } from "react-router";

const BankDetailsScreen = () => {
  const { formik } = useBankScreen();
  const [loading, setLoading] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(formik.values);

  const getBankdetails = useSelector(
    (state: RootState) => state.merchantForm.bankDetails
  ) as any;
  const userDetails = useSelector(
    (state: RootState) => state.appLayout.userDetails
  ) as any;

  const handleProceedClick = async (accountNumber: string, ifsc: string) => {
    setLoading(true);
    try {
      const response = await verifyBankDetails(accountNumber, ifsc);
      if (response.success) {
        dispatch(
          updateBankDetails({
            ...response.data.result,
            accountNumber,
            isBankVerified: true,
          })
        );
        console.log("Bank Details response:", response.data);
      }
    } catch (error) {
      toast.error("Something went wrong while verifying bank details.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetClick = () => {
    formik.resetForm();
    dispatch(resetBankDetails());
    setPermissionGranted(false);
  };

  const handleSubmit = async () => {
    if (!formik.isValid) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!getBankdetails.isBankVerified) {
      toast.error("Please verify your Bank Details before submitting.");
      return;
    }

    try {
      setLoading(true);
      const changedFields = getChangedFields(initialValues, formik.values);
      const data: any = {
        bankDetails: {
          ...getBankdetails,
          updatedItems: changedFields,
        },
      };

      const status = containsPropertyMerchant(userDetails, "bankDetails");

      if (status === "UPDATE") {
        data.kycStatus = "UPDATE";
      } else if (status === "TRUE") {
        data.kycStatus = "COMPLETED";
      }

      const response = await appendDetails(
        userDetails.userType,
        userDetails.userId,
        data
      );

      if (response?.message === "Item updated successfully") {
        toast.success("Bank details updated successfully!");
        dispatch(updateInitialState(response?.updatedItem));
        setInitialValues(formik.values);
        navigate("/organisation-business-details");
        window.location.reload();
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error(
        "Failed to update details. Please check your network and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      <Box>
        <Typography variant="h5" mb={2}>
          Bank Details
          <Typography
            variant="subtitle1"
            fontFamily={"Inter"}
            fontWeight={"500"}
            fontSize={16}
            display={"inline"}
            ml={1}
          >
            (Company A/C to be used for Merchant Payments)
          </Typography>{" "}
        </Typography>
      </Box>

      <Box
        border={1}
        borderColor="rgba(0,0,0,0.2)"
        borderRadius={2.5}
        flex={1}
        p={3}
      >
        <DynamicInput
          formik={formik}
          name="accountNumber"
          label="Account Number*"
          type="password"
          placeholder="Enter your Account Number"
          inputProps={{
            maxLength: 18,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          disabled={getBankdetails.isBankVerified} // Disable if already verified
          disableCopy
        />

        <Box display="flex" flexDirection="row">
          <DynamicInput
            formik={formik}
            name="confirmaccountNumber"
            label="Confirm Account Number*"
            type="text"
            placeholder="Re-enter your Account Number"
            inputProps={{
              maxLength: 18,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            disabled={getBankdetails.isBankVerified} // Disable if already verified
            disableCopy
          />
        </Box>

        <DynamicInput
          formik={formik}
          name="IFSCcode"
          label="IFSC Code*"
          type="text"
          placeholder="Enter your IFSC Code"
          disabled={getBankdetails.isBankVerified} // Disable if already verified
          disableCopy
        />

        {/* Permission Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={permissionGranted}
              onChange={(e) => setPermissionGranted(e.target.checked)}
              disabled={getBankdetails.isBankVerified} // Disable if already verified
            />
          }
          label="Allow fetching your details from the bank"
        />

        {/* Submit Button */}
        <Box
          textAlign="right"
          display="flex"
          justifyContent="start"
          columnGap={2}
          mt={3}
        >
          <WhiteButtonComponent
            onClick={handleResetClick}
            btnText="Reset"
            sx={{ padding: "10px 40px" }}
          />
          {!getBankdetails.isBankVerified && (
            <BlackButtonComponent
              onClick={() =>
                handleProceedClick(
                  formik.values.accountNumber,
                  formik.values.IFSCcode
                )
              }
              btnText={loading ? "Submitting..." : "Submit"}
              sx={{ padding: "10px 40px" }}
              disabled={loading || !permissionGranted} // Disabled unless permission is granted
            />
          )}
        </Box>

        {/* Show BankDetailsForm after verification */}
        {getBankdetails.isBankVerified && (
          <BankDetailsForm
            formik={formik}
            loading={loading}
            submit={handleSubmit}
          />
        )}
      </Box>
    </Box>
  );
};

export default BankDetailsScreen;
